import type { FeatureContext } from './featureContext';
import { initHud } from '../hud';

/** Injectable seams for unit tests; defaults are the real modules. */
export interface HudInstallDeps {
  initHud?: typeof initHud;
}

/**
 * The bottom progression HUD (Cosmetic Progression Slice D, ADR-0049): an XP fill
 * bar, a "M:SS to next unlock" countdown, and the player's own total online time.
 * The server pushes the authoritative `totalOnlineMs` as an anchor on login and at
 * each accrual checkpoint; the HUD snaps to each anchor and extrapolates forward
 * between them, ticked from the render loop. It stays hidden until the first anchor
 * reveals it (so it never shows over the splash).
 *
 * The owner-only gold anchor (Rat Swarm Slice F, ADR-0119/0121) also lands here:
 * the HUD's gold row shows a reserved balance plain (durable) or an anonymous
 * session's this-event gold greyed with the reserve nudge - and the balance is
 * mirrored into the shared state so the duel/dice wager prompts can validate a
 * stake (a durable balance is spendable; anonymous is honor-only, zero wager).
 *
 * The HUD also provides the shared bottom-centre-band suppression sink the Arena
 * run HUD drives (the run HUD takes over the slot while a run is live).
 */
export function installHud(ctx: FeatureContext, deps: HudInstallDeps = {}): void {
  const hud = (deps.initHud ?? initHud)({
    onOpenChange: (open) => ctx.toolbar.setOpen('quests', open),
  });
  ctx.toolbar.register('quests', () => hud.setOpen(false));
  ctx.state.provideHudSuppression((suppressed) => hud.setSuppressed(suppressed));
  ctx.events.on('onlineTime', (totalOnlineMs) => hud.setOnlineTime(totalOnlineMs));
  ctx.events.on('goldUpdate', (gold, durable) => {
    // Capture the balance for the wager prompts: a durable (reserved) balance is
    // spendable on a stake; an anonymous session (durable: false) is honor-only.
    ctx.state.setGold(durable ? gold : 0, durable);
    hud.setGold(gold, durable);
  });
  // Tick the HUD each frame: it extrapolates the online tally forward from the last
  // server anchor, so the XP bar fills smoothly and the countdown ticks down between
  // checkpoints. A no-op until the first anchor arrives.
  ctx.onFrame(() => hud.sync());
}
